
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useTheme } from "styled-components";
import { colors, getBreakPoint, useScreenWidth, Divider, Modal, SkeletonLoader, PaddingBottom40 } from "oolib";
// import { renderRTEPlaceholderForOolibPreview } from "../../../../utilsOolib/renderRTEPlaceholderForOolibPreview";
import AspectRatioShell from "../AspectRatioShell";
import { ConfirmDeleteModal } from "./comps/ConfirmDeleteModal";
import { EditToolbar } from "./comps/EditToolbar";
import { ImageActionsMenu } from "./comps/ImageActionsMenu";
import { ImageReposManager } from "./comps/ImageReposManager";
import { StyledFullWidthImgWrapper, StyledImageEditToolbar, StyledImg } from "./styled";
import { smallImageInputUIConditionsMet } from "./utils/smallImageInputUIConditionsMet";
import { useReorientImage } from "./utils/useReorientImage";
import LexicalTextEditor from "@/components/inputs/TextEditor/LexicalTextEditor";

/**
 * fiddle for dealing with rotation: https://jsfiddle.net/wunderbart/w1hw5kv1/
 */

export function ImageEditor({
  id,
  readOnly,
  value : imageData,
  defaultImageSpread,
  handleSingleImageDataChange,
  handleDeleteSingleImage,
  handleReplaceSingleImage,
  aspectRatio,
  invert,
  handleUpload,
  setImageUnderEdit, //comes from ImageInput context if used inside it
  disableImageBorder,
  stretchToFullHeight,
  containerShape,
  omitEditorTools,
  enableCaptions,
  imageTags,

  //sent by parent ImageEditor to child (modal) ImageEditor
  underEditImageData: _underEditImageData, //ImageEditor passes this to its recursive self when opening in popup
  setUnderEditImageData: _setUnderEditImageData,
  isInPopUp,
  isInRTE,
  handleDragStart
}) {

  // console.log({imageTags})
  const theme = useTheme()
  const {
    // LexicalTextEditor,
    lexicalTextEditorHasValue = () => false, //we are just defaulting to false for oolib preview. Meaning, when previewing in oolib, since this function doesnt exist (as it has to be provided by okf-fe), we will always return false from it.
    TagsInputSingle,
    TagsInputMulti,
    //backwards compat
    richTextHasValue = () => false,
    allTpls = [] 
  } = theme || {}; //temporarily passed thru from okf

  const [underEditImageData, setUnderEditImageData] = useState(
    _underEditImageData || undefined
  );

  const screenWidth = useScreenWidth()

  //set state in parent imageeditor
  useEffect(() => {
    _setUnderEditImageData && _setUnderEditImageData(underEditImageData);
  }, [underEditImageData]);

  const props = arguments[0];
  
  /** rotation */

  /**
   * if underEditImageData exists, render the temp 'formattings' as well,
   * which are stored in underEditImageData.
   *
   * underEditImageData is merged into imageData, only if the 'formattings'
   * are 'saved'
   */

  const {
    publicUrl,
    imageSpread = defaultImageSpread,
    imageRotate = 0,
    cropX = 0,
    cropY = 0,
  } = underEditImageData || imageData;

  /**
   * repos management
   */

  const wrapperRef = useRef(null);
  const renderedImageRef = useRef(null);

  const isFirstMount = useRef(true);

  let [fullWidthOrFullHeightImg, setFullWidthOrFullHeightImg] =
    useState(undefined);

  const { reorientedImageTempUrl, imageLazyLoading } = useReorientImage({
    imageRotate,
    setFullWidthOrFullHeightImg,
    setUnderEditImageData,
    publicUrl,
    aspectRatio,
    underEditImageData,
    isFirstMount,
    handleDeleteSingleImage,
    readOnly
  });

  const [showDeleteImageModal, setShowDeleteImageModal] = useState(undefined);

  const inputRef = useRef(null);

  const [editorStyle, setEditorStyle] = useState(undefined); //alt: modal

  useEffect(() => { isFirstMount.current = false },[])

  

  const decideEditorStyle = () => {
    let { width, height } = wrapperRef.current.getBoundingClientRect();

    if (isInPopUp) {
      setEditorStyle("toolbar");
    } else if (smallImageInputUIConditionsMet({width,height}) || containerShape === 'circle') {
      setEditorStyle("modal");
    } else {
      setEditorStyle("toolbar");
    }
  };

  const modalStyleEditorIsCurrentlyOpen = () =>
    editorStyle === "modal" && !!underEditImageData;

  const genModalStyleEditor = () => (
    <Modal 
      title={"Edit Image"} 
      fitToContentHeight={screenWidth >= getBreakPoint('sm') ? true : false}  
      desktopWidth="500px">
          <PaddingTop40>
            <ImageEditor
              {...props}
              id='insideModal'
              underEditImageData={underEditImageData}
              setUnderEditImageData={setUnderEditImageData}
              isInPopUp={true}
              // stretchToFullHeight
            />
           
          </PaddingTop40>
        
    </Modal>
  );

  const genToolbarStyledEditor = () => (
    <EditToolbar
      {...{
        setUnderEditImageData,
        underEditImageData,
        imageData,
        handleSingleImageDataChange,
        setImageUnderEdit, //comes from ImageInput context if used inside it
        imageSpread,
        publicUrl,
        defaultImageSpread,
        omitEditorTools,
        isInPopUp
      }}
    />
  );

  return (
    <div
      id={id && id+'_ImageEditor'}
      style={{
        position: "relative",
        height: stretchToFullHeight ? "100%" : "auto",
      }}
    >
      {imageLazyLoading && !modalStyleEditorIsCurrentlyOpen() ? (
        <AspectRatioShell
          {...{
            key: "loaderShell",
            aspectRatio,
            stretchToFullHeight,
            containerShape,
            disableImageBorder,
          }}
        >
          <SkeletonLoader style={{ height: "100%" }} />
        </AspectRatioShell>
      ) : (
        <Fragment>
          <AspectRatioShell
            {...{
              id,
              invert,
              key: "imageShell", //by adding key we are forcing this AspectRatioShell to rerender, so that decideEditorStyle() calculation happens
              aspectRatio,
              stretchToFullHeight,
              containerShape,
              disableImageBorder,
              onShellPrepared: decideEditorStyle,
            }}
          >
            {showDeleteImageModal && (
              <ConfirmDeleteModal
                {...{
                  showDeleteImageModal,
                  setShowDeleteImageModal,
                  handleDeleteSingleImage,
                }}
              />
            )}
            <StyledFullWidthImgWrapper
              ref={wrapperRef}
              invert={invert}
              disableImageBorder={disableImageBorder}
            >
              <StyledImg
                {...{
                  ref: renderedImageRef,
                  fullWidthOrFullHeightImg,
                  src: reorientedImageTempUrl || publicUrl,
                  imageSpread,
                  cropX,
                  cropY,
                }}
                onTouchStart={handleDragStart && handleDragStart}
                onMouseDown={handleDragStart && handleDragStart}
              />
              {underEditImageData !== undefined &&
                !omitEditorTools.includes("reposition") && (
                  <ImageReposManager
                    {...{
                      fullWidthOrFullHeightImg,
                      wrapperRef,
                      renderedImageRef,
                      setUnderEditImageData,
                    }}
                  />
                )}
            </StyledFullWidthImgWrapper>
          </AspectRatioShell>
          {!readOnly && (
            <StyledImageEditToolbar
              {...{
                underEditImageData,
                containerShape,
                isInPopUp,
              }}
            >
              {underEditImageData === undefined ? (
                <ImageActionsMenu
                  {...{
                    id,
                    invert,
                    setUnderEditImageData,
                    setImageUnderEdit,
                    inputRef,
                    setShowDeleteImageModal,
                    publicUrl,
                    imageData,
                    handleReplaceSingleImage,
                    handleUpload,
                    isInRTE
                  }}
                />
              ) : editorStyle === "toolbar" ? (
                genToolbarStyledEditor()
              ) : (
                editorStyle === "modal" && genModalStyleEditor()
              )}
            </StyledImageEditToolbar>
          )}
          { enableCaptions && (
             readOnly && 
             !lexicalTextEditorHasValue(imageData.caption) &&
             !richTextHasValue(imageData.caption) //backwards compat cuz its possible caption data might be in draftjs shape
             ? null
             : <Fragment>
                  <Divider invert={invert}/>
                  <div
                    style={{
                      borderRadius: "0.2rem",
                      padding: readOnly ? "1rem" : "2rem",
                      backgroundColor: invert ? colors.greyColor90 : colors.greyColor5,
                    }}
                  >
                    <LexicalTextEditor
                          key={`${id}_caption`}
                          id={`${id}_caption`}
                          variant='simple'
                          disableNewline
                          invert={invert}
                          placeholder="type caption for image (optional)"
                          readOnly={readOnly}
                          charLimit={100}
                          typo="SANS_3"
                          placeholderColor={invert ? colors.greyColor70 : colors.greyColor40}
                          value={imageData.caption}
                          onChange={(id, val) => {
                            handleSingleImageDataChange({
                              publicUrl,
                              imageData: { ...imageData, caption: val },
                            });
                          }}
                          />
                    
                  </div>
                  {readOnly && <Divider invert={invert}/>}
                  </Fragment>
                )}

                {
                  imageTags?.enable &&
                  imageTags?.config?.map(({comp, tagCategory}) => {
                    const compsLookup = {
                      TagsInputMulti,
                      TagsInputSingle
                    }
                    const Comp = compsLookup[comp];
                    if(!Comp) return <div>placeholder for tag input</div>
                    return (
                      <Comp
                        label={allTpls.find(tpl => tpl.kp_content_type === tagCategory)?.general?.content?.title || tagCategory}
                        popOutOfOverflowHiddenParent
                        tagType={tagCategory}
                        value={imageData.tags?.[tagCategory]}
                        onChange={(id, val) => {
                          handleSingleImageDataChange({
                            publicUrl,
                            imageData: { ...imageData, tags: {
                              ...(imageData.tags || {}),
                              [tagCategory]: val
                            }  },
                          });
                        }}
                        readOnly={readOnly}
                        
                        />
                  )})
                }
                </Fragment>
      )}
    </div>
  );
}
